import { usGameSportForBoard, usPeriodForMarketType, usPeriodWinner, type UsPeriod } from './sports';
/**
 * Board ↔ Polymarket US mapping for the shared dashboard (#987).
 *
 * Pure and exact. A US market joins a board game only by the producer's
 * canonical game key and named YES team; nothing is inferred from titles,
 * team order or prices. Anything ambiguous maps to null (not shown), never to
 * a guess, because the mapping chooses which outcome an order buys.
 */
import type { BookLevel } from '@/types';
import type { UsBookSide, UsDiscoveryMarket, UsMarketBookView } from './marketClient';
import { sameUsGamePropTarget, usGamePropTarget, type UsGamePropTarget } from './gamePropTarget';
import { sameUsPlayerTarget, usPlayerTarget, type UsPlayerTarget } from './playerTarget';
import type { UsFullGameTotalFamily } from './totalTarget';

/** Consumer integration support, not evidence of a listed/current market. */
export function usVenuePlanForBoardKey(boardKey: string): 'current' | 'pending' | null {
  return usGameKeyForBoardKey(boardKey) ? 'current' : null;
}

/** The US canonical game key for a board row key, or null when the row is not
 * a supported full-game key in the five-segment `sport|date|gameNo|LO|HI` form. */
export function usGameKeyForBoardKey(boardKey: string): string | null {
  const parts = boardKey.split('|');
  if (parts.length < 5) return null;
  const sport = usGameSportForBoard(parts[0])?.toLowerCase();
  if (!sport || !/^\d{4}-\d{2}-\d{2}$/.test(parts[1]) || !/^\d+$/.test(parts[2])
    || !/^[A-Z0-9]{1,8}$/.test(parts[3]) || !/^[A-Z0-9]{1,8}$/.test(parts[4])) return null;
  return [sport, parts[1], parts[2], parts[3], parts[4]].join('|');
}

/** A single exact market, none, or a refused ambiguity. */
export type UsPick<M> =
  | { kind: 'market'; market: M }
  | { kind: 'none' }
  | { kind: 'ambiguous'; count: number };
export type UsMoneylinePick = UsPick<UsDiscoveryMarket>;
/** The fields `pickUsSpread` reads: a catalogue row, or #1231's live line quote. */
export type UsSpreadCandidate = Pick<UsDiscoveryMarket, 'gameKey' | 'marketType' | 'sportsMarketType' | 'line' | 'yesTeam' | 'period'>;

/** The single full-game US moneyline for this game, whose YES team is one of
 * the row's two teams. Two or more candidates is refused, not ranked. */
export function pickUsMoneyline(markets: readonly UsDiscoveryMarket[], usGameKey: string,
  awayCode: string | null | undefined, homeCode: string | null | undefined,
  period: UsPeriod = 'full', selectedTeam?: string | null): UsMoneylinePick {
  if (!awayCode || !homeCode) return { kind: 'none' };
  const matches = markets.filter(m => m.gameKey === usGameKey
    && (period === 'full' ? m.marketType === 'moneyline' && usPeriodForMarketType(m.sportsMarketType) === 'full'
      : m.marketType === 'props' && usPeriodWinner(m.sportsMarketType, m.sport) === period
        && selectedTeam != null && m.yesTeam === selectedTeam)
    && (period === 'full' ? m.period === undefined || m.period === 'full' : m.period === period)
    && (m.yesTeam === awayCode || m.yesTeam === homeCode));
  if (matches.length === 0) return { kind: 'none' };
  if (matches.length > 1) return { kind: 'ambiguous', count: matches.length };
  return { kind: 'market', market: matches[0] };
}

/** Which board line a US market must match. A spread names the favored team
 * (the board market's YES is "favored wins by more than `magnitude`"); a
 * full-game total names its supported native family and exact threshold
 * (#1030: the board's Over/Under side is chosen in the panel, not here).
 * Native player/game props retain their exact identity; other contracts not
 * integrated here remain `unsupported`, even when the producer lists them. */
export type UsLineTarget =
  | { kind: 'moneyline'; period?: UsPeriod }
  | { kind: 'spread'; favoredCode: string; otherCode: string; magnitude: number }
  | { kind: 'total'; family: UsFullGameTotalFamily; threshold: number }
  | { kind: 'player-prop'; target: UsPlayerTarget }
  | { kind: 'game-prop'; target: UsGamePropTarget }
  | { kind: 'unsupported' };

export function pickUsGameProp(markets: readonly UsDiscoveryMarket[], gameKey: string, target: UsGamePropTarget): UsMoneylinePick {
  const matches = markets.filter(m => { const candidate = usGamePropTarget(m);
    return m.gameKey === gameKey && candidate !== null && sameUsGamePropTarget(candidate, target); });
  return matches.length === 0 ? { kind: 'none' } : matches.length > 1
    ? { kind: 'ambiguous', count: matches.length } : { kind: 'market', market: matches[0] };
}

export function pickUsPlayerProp(markets: readonly UsDiscoveryMarket[], gameKey: string, target: UsPlayerTarget): UsMoneylinePick {
  const matches = markets.filter(m => {
    const candidate = usPlayerTarget(m);
    return m.gameKey === gameKey && candidate !== null && sameUsPlayerTarget(candidate, target);
  });
  return matches.length === 0 ? { kind: 'none' } : matches.length > 1
    ? { kind: 'ambiguous', count: matches.length } : { kind: 'market', market: matches[0] };
}

/** The single full-game US spread for this exact board line. US lists one YES
 * team per game with signed strikes: YES team F at "-m" is the same outcome
 * as the board's "F wins by over m", and the other team's "+m" market is its
 * complement (the host maps outcomes through the favored team). Anything
 * else, or more than one candidate, is not shown. */
export function pickUsSpread<M extends UsSpreadCandidate = UsDiscoveryMarket>(markets: readonly M[], usGameKey: string,
  target: { favoredCode: string; otherCode: string; magnitude: number }): UsPick<M> {
  if (!(target.magnitude > 0)) return { kind: 'none' };
  const matches = markets.filter(m => m.gameKey === usGameKey && m.marketType === 'spreads'
    && /full_game_spread$/.test(m.sportsMarketType) && m.line !== null
    && (m.period === undefined || m.period === 'full')
    && ((m.yesTeam === target.favoredCode && Number(m.line) === -target.magnitude)
      || (m.yesTeam === target.otherCode && Number(m.line) === target.magnitude)));
  if (matches.length === 0) return { kind: 'none' };
  if (matches.length > 1) return { kind: 'ambiguous', count: matches.length };
  return { kind: 'market', market: matches[0] };
}

/** Which US binary outcome a board "team — YES/NO" selection is. */
export function usOutcomeFor(teamCode: string, yesTeam: string, boardSide: 'yes' | 'no'): 'yes' | 'no' {
  const teamIsYes = teamCode === yesTeam;
  return boardSide === 'yes' ? (teamIsYes ? 'yes' : 'no') : (teamIsYes ? 'no' : 'yes');
}

/** USD decimal string → cents with at most two decimals (display only). */
export function usdToCents(priceUsd: string): number {
  return Math.round(Number(priceUsd) * 10_000) / 100;
}

/** Cents (as typed, e.g. "57.5") → exact USD decimal string, or null when
 * the value is not a positive price with at most two cent decimals. */
export function centsToUsd(cents: string): string | null {
  const m = /^(\d{1,2})(?:\.(\d{1,2}))?$/.exec(cents.trim());
  if (!m) return null;
  const hundredths = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  if (hundredths <= 0 || hundredths >= 10_000) return null;
  const whole = String(hundredths).padStart(4, '0');
  return `0.${whole}`.replace(/0+$/, '').replace(/\.$/, '');
}

export interface UsBookDisplay {
  bids: BookLevel[]; asks: BookLevel[];
  /** true when a side's depth is unknown or cut off at the published bound. */
  partial: boolean;
}

/** Ladder for one outcome: its own resting bids, and asks derived from the
 * opposite outcome's bids at the binary complement (1 − p). */
export function usBookForOutcome(book: Pick<UsMarketBookView, 'yes' | 'no'>, outcome: 'yes' | 'no'): UsBookDisplay {
  const own: UsBookSide = outcome === 'yes' ? book.yes : book.no;
  const other: UsBookSide = outcome === 'yes' ? book.no : book.yes;
  const bids = own.bids.map(l => ({ priceCents: usdToCents(l.priceUsd), size: Number(l.size) }));
  const asks = other.bids.map(l => ({ priceCents: Math.round((100 - usdToCents(l.priceUsd)) * 100) / 100,
    size: Number(l.size) }));
  return { bids, asks, partial: !own.complete || !other.complete || own.truncated || other.truncated };
}
